import axios from 'axios';

// Centralized API instance with proper baseURL handling
const rawBase = (process.env.REACT_APP_BACKEND_URL ?? '').trim();
const baseURL = rawBase.replace(/\/+$/, '').replace(/\/api$/i, '');
export const api = axios.create({
  baseURL: baseURL || '',
  timeout: 60000, // 60 second timeout to prevent indefinite hangs
});

// Add auth token from localStorage to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hoa_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add multi-HOA and multi-property context headers
  const activeHoaId = localStorage.getItem('active_hoa_id');
  const activePropertyId = localStorage.getItem('active_property_id');
  
  if (activeHoaId) {
    config.headers['X-HOA-ID'] = activeHoaId;
  }
  
  if (activePropertyId) {
    config.headers['X-Property-ID'] = activePropertyId;
  }
  
  // Debug logging to identify request issues
  const url = `${config.baseURL || ''}${config.url}`;
  console.log('[API Request]', config.method?.toUpperCase(), url, { 
    params: config.params,
    hasData: !!config.data,
    headers: { Authorization: config.headers.Authorization ? '***' : 'none', 'X-HOA-ID': config.headers['X-HOA-ID'] }
  });
  
  return config;
});

// Enhanced error logging and handling
api.interceptors.response.use(
  (response) => {
    console.log('[API Response]', response.config.method?.toUpperCase(), response.config.url, 'Status:', response.status);
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url;
    const method = error?.config?.method?.toUpperCase();
    
    if (status === 502) {
      console.error('[API 502 Bad Gateway]', method, url, 'This usually indicates a backend timeout or crash');
      console.error('Request payload:', error?.config?.data);
    } else if (error.code === 'ECONNABORTED') {
      console.error('[API Timeout]', method, url, 'Request exceeded timeout limit');
    } else {
      console.error('[API Error]', method, url, 'Status:', status, 'Message:', error?.response?.data || error.message);
    }
    
    return Promise.reject(error);
  }
);

// Helper function for better error messages
export const showApiError = (error, fallbackMsg = 'Request failed') => {
  const detail = error.response?.data?.detail;
  const errors = error.response?.data?.errors;
  return `${fallbackMsg}: ${detail || error.message}${
    errors ? `\n- ${[].concat(errors).join('\n- ')}` : ''
  }`;
};

// Chat-specific API functions with correct endpoints
export const chatApi = {
  // Get all chats
  getChats: (userId, hoaId, chatType = null, status = 'active') => api.get('/api/chat/', {
    params: { 
      user_id: userId, 
      hoa_id: hoaId, 
      ...(chatType && { chat_type: chatType }),
      ...(status && { status: status })
    }
  }),
  
  // Get specific chat details
  getChat: (chatId, userId, hoaId) => api.get(`/api/chat/${chatId}`, {
    params: { user_id: userId, hoa_id: hoaId }
  }),

  // Get messages
  getMessages: (chatId, userId, hoaId, limit=50, before_message_id=null) => api.get(`/api/chat/${chatId}/messages`, {
    params: { user_id: userId, hoa_id: hoaId, limit, before_message_id }
  }),
  
  // Create new chat
  createChat: (chatData, userId, hoaId) => 
    api.post('/api/chat/', chatData, { params: { creator_id: userId, hoa_id: hoaId } }),
  
  // Delete chat (board members only)
  deleteChat: (chatId, userId, hoaId) => 
    api.delete(`/api/chat/${chatId}?user_id=${userId}&hoa_id=${hoaId}`),
  
  // Fix chat participants (auto-join)
  fixParticipants: (userId, hoaId) => 
    api.post(`/api/chat/fix-participants?user_id=${userId}&hoa_id=${hoaId}`),
  
  // Get chat members list (use participants endpoint)
  getChatMembers: (chatId, hoaId) => api.get(`/api/chat/${chatId}/participants`, { params: { hoa_id: hoaId } }),
  
  // Get chat members with authentication (better user enrichment)
  getChatMembersAuth: (chatId, hoaId, token) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    return api.get(`/api/chat/${chatId}/participants`, { 
      params: { hoa_id: hoaId },
      headers
    });
  },

  // Archive chat
  archiveChat: (chatId) => api.patch(`/api/chat/${chatId}/archive`),

  // Send text message
  sendTextMessage: (chatId, content, userId, hoaId) => 
    api.post(`/api/chat/${chatId}/messages`, { content }, {
      params: { sender_id: userId, user_id: userId, hoa_id: hoaId }
    }),
  
  // Send poll message
  sendPollMessage: (chatId, { question, options, allow_multiple_votes = false, expires_at }, userId, hoaId) =>
    api.post(`/api/chat/${chatId}/messages/poll`, {
      content: question,
      poll_options: options,
      allow_multiple_votes,
      expires_at: expires_at ? new Date(expires_at).toISOString() : null
    }, { params: { sender_id: userId, user_id: userId, hoa_id: hoaId } }),
  
  // Send RSVP event message
  sendEventMessage: (chatId, { description, title, starts_at, location, rsvp_deadline, max_attendees }, userId, hoaId) =>
    api.post(`/api/chat/${chatId}/messages/rsvp-event`, {
      content: description || '',
      event_title: title,
      event_datetime: new Date(starts_at).toISOString(),
      event_location: location || null,
      rsvp_deadline: rsvp_deadline ? new Date(rsvp_deadline).toISOString() : null,
      max_attendees: max_attendees ?? null
    }, { params: { sender_id: userId, user_id: userId, hoa_id: hoaId } }),
  
  // Vote on poll
  voteOnPoll: (chatId, messageId, userId, hoaId, optionIndex, add = true) => 
    api.post(`/api/chat/${chatId}/messages/${messageId}/vote`,
      { option_index: optionIndex, action: add ? 'add' : 'remove' },
      { params: { user_id: userId, hoa_id: hoaId } }
    ),
  
  // RSVP to event
  rsvpToEvent: (chatId, messageId, userId, hoaId, { rsvp_status, guest_count = 0, comment = null }) => 
    api.post(`/api/chat/${chatId}/messages/${messageId}/rsvp`,
      { rsvp_status, guest_count, comment },
      { params: { user_id: userId, hoa_id: hoaId } }
    ),

  // Update chat settings
  updateChat: (chatId, patch, userId, hoaId) =>
    api.patch(`/api/chat/${chatId}`, patch, { params: { user_id: userId, hoa_id: hoaId } }),

  // Delete chat (board members only) - alternative method
  deleteChatAlt: (chatId, userId, hoaId) =>
    api.delete(`/api/chat/${chatId}`, { params: { user_id: userId, hoa_id: hoaId } }),

  // Reconcile chat participants
  reconcileParticipants: ({ hoaId, chatId = null, committeeId = null }) =>
    api.post('/api/chat/reconcile-participants', null, { 
      params: { hoa_id: hoaId, chat_id: chatId, committee_id: committeeId } 
    }),

  // List participants with details
  listParticipants: (chatId, hoaId) => 
    api.get(`/api/chat/${chatId}/participants`, { params: { hoa_id: hoaId } }),

  // Add homeowner to contact chat
  addHomeownerToContact: (chatId, payload, hoaId) =>
    api.post(`/api/chat/${chatId}/add-homeowner`, payload, { params: { hoa_id: hoaId } }),
};

// Forms API functions
export const formsApi = {
  // Get form templates
  getTemplates: (hoaId) => api.get('/api/forms/templates', { params: { hoa_id: hoaId } }),
  
  // Submit form
  submitForm: (templateKey, answers, hoaId) => api.post('/api/forms/submit', {
    template_key: templateKey,
    answers
  }, { params: { hoa_id: hoaId } }),
  
  // Get submissions (user's own or all for board members)
  getSubmissions: (hoaId, filters = {}) => api.get('/api/forms/submissions', { 
    params: { hoa_id: hoaId, ...filters } 
  }),
  
  // Get submission details
  getSubmissionDetails: (submissionId, hoaId) => api.get(`/api/forms/submissions/${submissionId}`, {
    params: { hoa_id: hoaId }
  }),
  
  // Update submission status (board members only)
  updateSubmissionStatus: (submissionId, updateData, hoaId) => 
    api.post(`/api/forms/submissions/${submissionId}/status`, updateData, {
      params: { hoa_id: hoaId }
    }),
  
  // Add note to submission (board members only)
  addSubmissionNote: (submissionId, note, isInternal, hoaId) => 
    api.post(`/api/forms/submissions/${submissionId}/notes`, {
      note, is_internal: isInternal
    }, { params: { hoa_id: hoaId } }),
  
  // Get forms dashboard data
  getDashboard: (hoaId) => api.get('/api/forms/dashboard', { params: { hoa_id: hoaId } }),
  
  // Setup default templates (board members only)
  setupDefaultTemplates: (hoaId) => api.post(`/api/forms/setup/${hoaId}`)
};

// Settings API functions
export const settingsApi = {
  // Get HOA Track settings
  getHOATrackSettings: (hoaId) => api.get('/api/settings/hoa-track', { params: { hoa_id: hoaId } }),
  
  // Update HOA Track settings
  updateHOATrackSettings: (hoaId, settings) => api.post('/api/settings/hoa-track', settings, { params: { hoa_id: hoaId } }),
  
  // Get committees
  getCommittees: (hoaId) => api.get(`/api/hoa/${hoaId}/committees`),
  
  // Get dashboard data for overview widget
  getDashboardData: (hoaId) => api.get('/api/settings/dashboard-data', { params: { hoa_id: hoaId } }),
  
  // Upload file
  uploadFile: (file, hoaId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/settings/upload-file', formData, {
      params: { hoa_id: hoaId },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Team Users API functions
export const teamUsersApi = {
  // Get pending team users for approval
  getPendingTeamUsers: (hoaId) => api.get('/api/team-users/pending', { params: { hoa_id: hoaId } }),
  
  // Approve team user
  approveTeamUser: (userId, hoaId) => api.post(`/api/team-users/${userId}/approve`, {}, { params: { hoa_id: hoaId } }),
  
  // Reject team user
  rejectTeamUser: (userId, reason, hoaId) => api.post(`/api/team-users/${userId}/reject`, { reason }, { params: { hoa_id: hoaId } }),
  
  // Get all team users
  getTeamUsers: (hoaId, role = null) => api.get('/api/team-users', { params: { hoa_id: hoaId, role } }),
  
  // Create team user
  createTeamUser: (userData, hoaId) => api.post('/api/team-users', userData, { params: { hoa_id: hoaId } }),
  
  // Bulk import team users
  bulkImportTeamUsers: (file, role, hoaId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/team-users/bulk-import', formData, {
      params: { hoa_id: hoaId, role },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Export team users
  exportTeamUsers: (hoaId, role = null) => api.get('/api/team-users/export', { params: { hoa_id: hoaId, role } }),
  
  // Update team user
  updateTeamUser: (userId, userData, hoaId) => api.put(`/api/team-users/${userId}`, userData, { params: { hoa_id: hoaId } }),
  
  // Delete team user
  deleteTeamUser: (userId, hoaId) => api.delete(`/api/team-users/${userId}`, { params: { hoa_id: hoaId } })
};

export default api;